import { useState, useRef, useEffect, useCallback } from 'react';
import { OBD_PIDS, ACTIVE_PIDS } from './obd-pids';
import { buildFastFrame, extractFrames, decodeMode01Response, pidStringToBytes, bytesToHex } from './bmw-dcan';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Custom hook managing OBD adapter connection, data polling, and logging.
 */
export default function useOBDConnection() {
  const [connectionMode, setConnectionMode] = useState('android');
  const [isConnected, setIsConnected] = useState(false);
  const [isLogging, setIsLogging] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [liveData, setLiveData] = useState({});
  const [logHistory, setLogHistory] = useState([]);
  const [error, setError] = useState(null);
  const [rawResponses, setRawResponses] = useState([]);

  // Settings
  const [thorIP, setThorIP] = useState('192.168.0.10');
  const [thorPort, setThorPort] = useState('35000');
  const [radioIP, setRadioIP] = useState('');
  const [radioPort, setRadioPort] = useState('35000');
  const [radioProtocol, setRadioProtocol] = useState('elm327');
  const [baudRate, setBaudRate] = useState('9600');
  // Direct Local Bridge (browser <-> local python bridge, bypasses cloud)
  const [useDirectBridge, setUseDirectBridge] = useState(false);
  const [bridgeUrl, setBridgeUrl] = useState('ws://127.0.0.1:8765');

  // Refs
  const wsRef = useRef(null);
  const portRef = useRef(null);
  const readerRef = useRef(null);
  const writerRef = useRef(null);
  const loggingInterval = useRef(null);
  const currentPidIndex = useRef(0);
  const responseBuffer = useRef('');
  const dcanBufferRef = useRef([]); // accumulating raw bytes for BMW D-CAN framing
  const isLoggingRef = useRef(false);
  const disconnectRef = useRef(null);
  // Watchdog: count RX events so we can tell the user if we're sending commands
  // but getting zero data back (wrong baud / K+DCAN cable / ignition off / etc).
  const rxCountRef = useRef(0);
  const watchdogTimeout = useRef(null);

  const addRawResponse = useCallback((msg) => {
    const time = new Date().toLocaleTimeString();
    setRawResponses(prev => [...prev.slice(-30), { time, msg }]);
    if (typeof msg === 'string' && msg.startsWith('RX:')) {
      rxCountRef.current += 1;
    }
  }, []);

  // --- Data processing ---

  const updateLiveData = useCallback((key, value, pidInfo) => {
    setLiveData(prev => ({
      ...prev,
      [key]: { value, timestamp: Date.now(), name: pidInfo.name, unit: pidInfo.unit }
    }));
    if (isLoggingRef.current) {
      setLogHistory(prev => [...prev, { timestamp: Date.now(), pid: key, value, unit: pidInfo.unit }]);
    }
  }, []);

  const processOBDResponse = useCallback((pid, response) => {
    // ELM327 responses can include: "SEARCHING..." prefix (first request after ATSP0),
    // echoed command (if ATE0 didn't stick on some USB clones), extra newlines, and
    // status messages like "NO DATA" / "STOPPED" / "BUS INIT". Find the actual
    // Mode 01 response (starts with "41 XX") ANYWHERE in the cleaned stream rather
    // than only at the start — previously any prefix dropped the sample.
    const cleaned = response.replace(/[\r\n\s>]/g, '').toUpperCase();
    const match = cleaned.match(/41[0-9A-F]{2,}/);
    if (!match) return;
    const body = match[0]; // e.g. "410C1AF8"
    const pidCode = body.substring(2, 4);
    const dataHex = body.substring(4);
    for (const [key, pidInfo] of Object.entries(OBD_PIDS)) {
      if (pidInfo.pid.substring(2, 4).toUpperCase() === pidCode) {
        const value = pidInfo.parse(dataHex);
        if (value !== null && !isNaN(value)) updateLiveData(key, value, pidInfo);
        break;
      }
    }
  }, [updateLiveData]);

  // --- Serial helpers ---

  const sendSerialCommand = useCallback(async (cmd) => {
    if (!writerRef.current) return;
    const encoder = new TextEncoder();
    await writerRef.current.write(encoder.encode(cmd + '\r'));
    addRawResponse(`TX: ${cmd}`);
  }, [addRawResponse]);

  const readSerialLoop = useCallback(async () => {
    const decoder = new TextDecoder();
    while (portRef.current?.readable && readerRef.current) {
      try {
        const { value, done } = await readerRef.current.read();
        if (done) break;
        responseBuffer.current += decoder.decode(value);
        if (responseBuffer.current.includes('>')) {
          const responses = responseBuffer.current.split('>');
          for (let i = 0; i < responses.length - 1; i++) {
            const resp = responses[i].trim();
            if (resp) {
              addRawResponse(`RX: ${resp}`);
              processOBDResponse(resp, resp);
            }
          }
          responseBuffer.current = responses[responses.length - 1];
        }
      } catch (err) {
        if (err.name !== 'TypeError' && process.env.NODE_ENV === 'development') console.error('Read error:', err);
        break;
      }
    }
  }, [addRawResponse, processOBDResponse]);

  // --- BMW D-CAN (K+DCAN cable) raw protocol helpers ---

  const processDCANFrame = useCallback((frame) => {
    const resp = decodeMode01Response(frame);
    if (!resp) return;
    for (const [key, pidInfo] of Object.entries(OBD_PIDS)) {
      if (pidInfo.pid.substring(2, 4).toUpperCase() === resp.pidCode) {
        const value = pidInfo.parse(resp.payloadHex);
        if (value !== null && !isNaN(value)) updateLiveData(key, value, pidInfo);
        break;
      }
    }
  }, [updateLiveData]);

  const readDCANLoop = useCallback(async () => {
    while (portRef.current?.readable && readerRef.current) {
      try {
        const { value, done } = await readerRef.current.read();
        if (done) break;
        if (value && value.length) {
          for (const b of value) dcanBufferRef.current.push(b);
          const { frames, rest } = extractFrames(dcanBufferRef.current);
          dcanBufferRef.current = rest;
          for (const frame of frames) {
            // Only frames addressed back to the tester (0xF1) are real ECU
            // responses; frames with target 0x12 are the cable's local echo.
            if (frame[1] === 0xF1) addRawResponse(`RX: ${bytesToHex(frame)}`);
            processDCANFrame(frame);
          }
        }
      } catch (err) {
        if (err.name !== 'TypeError' && process.env.NODE_ENV === 'development') console.error('DCAN read error:', err);
        break;
      }
    }
  }, [addRawResponse, processDCANFrame]);

  const sendDCANFrame = useCallback(async (dataBytes) => {
    if (!writerRef.current) return;
    const frame = buildFastFrame(dataBytes);
    await writerRef.current.write(frame);
    addRawResponse(`TX: ${bytesToHex(frame)}`);
  }, [addRawResponse]);

  const connectDCAN = useCallback(async () => {
    if (!('serial' in navigator)) { setError('Web Serial not supported. Use Chrome or Edge on desktop.'); return; }
    setError(null); setConnectionStatus('connecting'); setRawResponses([]);
    dcanBufferRef.current = [];
    try {
      const port = await navigator.serial.requestPort();
      portRef.current = port;
      addRawResponse('Opening K+DCAN cable at 115200 8N1 (raw BMW-FAST)...');
      await port.open({ baudRate: 115200, dataBits: 8, stopBits: 1, parity: 'none', flowControl: 'none' });
      readerRef.current = port.readable.getReader();
      writerRef.current = port.writable.getWriter();
      readDCANLoop();
      setIsConnected(true); setConnectionStatus('connected');
      addRawResponse('Cable open. Turn ignition to position II (engine running for live values), then Start Logging.');
      // Probe RPM once so a correctly wired cable shows an immediate RX frame.
      await sendDCANFrame([0x01, 0x0C]);
    } catch (err) {
      const raw = err?.message || String(err);
      let msg = raw;
      if (/failed to open|already open|access is denied|in use|open serial/i.test(raw)) {
        msg = 'Could not open the cable — the COM port is busy. Windows only lets ONE program use the cable at a time. Fixes: (1) Fully close INPA / Tool32 / ISTA / EDIABAS (check Task Manager for TOOL32.EXE, INPA.exe, ediabas). (2) Close any other browser tab connected to the cable. (3) Unplug the cable, wait 3s, replug, then Connect again.';
      } else if (/no port selected|cancelled|The user aborted/i.test(raw)) {
        msg = 'No COM port selected. Click Connect again and choose the FTDI "USB Serial Port" for your K+DCAN cable.';
      }
      setError(msg);
      setConnectionStatus('error');
      addRawResponse(`ERROR: ${msg}`);
    }
  }, [addRawResponse, readDCANLoop, sendDCANFrame]);

  // --- WebSocket connect (shared by WiFi + Android Radio) ---

  const connectWebSocket = useCallback((ip, port, label, extraPayload = {}) => {
    setError(null);
    setConnectionStatus('connecting');
    setRawResponses([]);
    const wsEndpoint = useDirectBridge ? bridgeUrl : `${WS_URL}/api/obd/ws`;
    addRawResponse(`Connecting to ${label} at ${ip}:${port} via ${useDirectBridge ? `bridge ${bridgeUrl}` : 'cloud'}...`);

    let ws;
    try {
      ws = new WebSocket(wsEndpoint);
    } catch (e) {
      setError(`Invalid WebSocket URL: ${wsEndpoint}`);
      setConnectionStatus('error');
      addRawResponse(`ERROR: ${e.message}`);
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      addRawResponse(`WebSocket connected, initializing ${label}...`);
      ws.send(JSON.stringify({ action: 'connect', ip, port: parseInt(port), ...extraPayload }));
    };
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'connected') { setIsConnected(true); setConnectionStatus('connected'); addRawResponse(data.message); }
      else if (data.type === 'disconnected') { setIsConnected(false); setConnectionStatus('disconnected'); addRawResponse(data.message); }
      else if (data.type === 'error') { setError(data.message); setConnectionStatus('error'); addRawResponse(`ERROR: ${data.message}`); }
      else if (data.type === 'response') { addRawResponse(`TX: ${data.cmd}`); addRawResponse(`RX: ${data.response}`); processOBDResponse(data.cmd, data.response); }
      else if (data.type === 'pid_data') { processOBDResponse(data.pid, data.response); }
    };
    ws.onerror = () => {
      const hint = useDirectBridge
        ? `WebSocket error - is the bridge running at ${bridgeUrl}?`
        : 'WebSocket connection error';
      setError(hint); setConnectionStatus('error'); addRawResponse(`ERROR: ${hint}`);
    };
    ws.onclose = () => { setIsConnected(false); setConnectionStatus('disconnected'); addRawResponse('Connection closed'); };
  }, [addRawResponse, processOBDResponse, useDirectBridge, bridgeUrl]);

  // --- Public connect methods ---

  const connectWiFi = useCallback(() => {
    connectWebSocket(thorIP, thorPort, 'Thor');
  }, [connectWebSocket, thorIP, thorPort]);

  const connectAndroidRadio = useCallback(() => {
    if (!radioIP) { setError('Please enter your Android Radio IP address'); setConnectionStatus('error'); return; }
    connectWebSocket(radioIP, radioPort, 'Android Radio', { protocol: radioProtocol });
  }, [connectWebSocket, radioIP, radioPort, radioProtocol]);

  const connectSerial = useCallback(async () => {
    if (!('serial' in navigator)) { setError('WebSerial not supported. Use Chrome or Edge.'); return; }
    setError(null); setConnectionStatus('connecting'); setRawResponses([]);
    try {
      const port = await navigator.serial.requestPort();
      portRef.current = port;
      const baud = parseInt(baudRate);
      addRawResponse(`Opening port at ${baud} baud...`);
      await port.open({ baudRate: baud, dataBits: 8, stopBits: 1, parity: 'none', flowControl: 'none' });
      readerRef.current = port.readable.getReader();
      writerRef.current = port.writable.getWriter();
      readSerialLoop();
      setConnectionStatus('initializing');
      addRawResponse('Initializing ELM327...');
      await delay(500); await sendSerialCommand('ATZ'); await delay(1000);
      for (const cmd of ['ATE0', 'ATL0', 'ATH0', 'ATS0', 'ATSP0']) { await sendSerialCommand(cmd); await delay(200); }
      setIsConnected(true); setConnectionStatus('connected'); addRawResponse('Connected!');
    } catch (err) { setError(err.message); setConnectionStatus('error'); }
  }, [baudRate, addRawResponse, sendSerialCommand, readSerialLoop]);

  const handleConnect = useCallback(() => {
    if (connectionMode === 'wifi') connectWiFi();
    else if (connectionMode === 'android') connectAndroidRadio();
    else if (connectionMode === 'dcan') connectDCAN();
    else connectSerial();
  }, [connectionMode, connectWiFi, connectAndroidRadio, connectDCAN, connectSerial]);

  // --- Disconnect ---

  const stopLogging = useCallback(() => {
    setIsLogging(false);
    isLoggingRef.current = false;
    if (loggingInterval.current) { clearInterval(loggingInterval.current); loggingInterval.current = null; }
    if (watchdogTimeout.current) { clearTimeout(watchdogTimeout.current); watchdogTimeout.current = null; }
  }, []);

  const disconnect = useCallback(async () => {
    stopLogging();
    if (wsRef.current) { wsRef.current.send(JSON.stringify({ action: 'disconnect' })); wsRef.current.close(); wsRef.current = null; }
    try {
      if (readerRef.current) { await readerRef.current.cancel(); readerRef.current.releaseLock(); readerRef.current = null; }
      if (writerRef.current) { writerRef.current.releaseLock(); writerRef.current = null; }
      if (portRef.current) { await portRef.current.close(); portRef.current = null; }
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error('Disconnect error:', err); }
    setIsConnected(false); setConnectionStatus('disconnected');
  }, [stopLogging]);

  disconnectRef.current = disconnect;
  useEffect(() => { const ref = disconnectRef; return () => { if (ref.current) ref.current(); }; }, []);

  // --- Logging ---

  const startLogging = useCallback(() => {
    if (!isConnected) return;
    setIsLogging(true); isLoggingRef.current = true; setLogHistory([]); currentPidIndex.current = 0;
    setError(null);
    rxCountRef.current = 0;
    // Watchdog: if we've been sending PIDs for 3s and received ZERO RX lines,
    // the adapter/cable/wiring is the problem — surface an actionable message
    // instead of silently showing "RECORDING · 0 samples" forever.
    if (watchdogTimeout.current) clearTimeout(watchdogTimeout.current);
    watchdogTimeout.current = setTimeout(() => {
      if (isLoggingRef.current && rxCountRef.current === 0) {
        const modeHint = connectionMode === 'dcan'
          ? 'K+DCAN raw: this needs a D-CAN car (2008+ E9x etc.) with ignition ON (position II). If you only see TX frames echoed back and no RX, the cable is passing your bytes but the ECU is not answering — check the cable is in D-CAN mode (not K-line jumper), the FTDI latency timer is set to 1ms in Device Manager, and ignition is on.'
          : connectionMode === 'serial'
          ? 'USB/Serial: try a different Baud Rate (9600 → 38400 → 115200). If your cable is K+DCAN (bundled with INPA/ISTA), switch to the BMW K+DCAN tab — it does NOT speak ELM327. Also verify ignition is ON (key to position II).'
          : connectionMode === 'android'
          ? 'Android Radio: verify the ProTool/Torque TCP server is still running and the IP/port match. Confirm the car\'s ignition is ON.'
          : 'Thor WiFi: ignition must be ON. Confirm your device is still on the Thor WiFi network.';
        setError(`Commands sent but no responses from the adapter. ${modeHint}`);
      }
    }, 3000);

    // Route by the actual open connection, NOT by connectionMode —
    // Android Radio also uses WebSocket (wifi + android both go through connectWebSocket).
    // Previous bug: connectionMode==='android' fell into the serial branch and silently
    // dropped every PID request because writerRef.current was null on WS connections.
    const wsOpen = wsRef.current?.readyState === WebSocket.OPEN;
    if (wsOpen) {
      loggingInterval.current = setInterval(() => {
        const pidKey = ACTIVE_PIDS[currentPidIndex.current];
        const pidInfo = OBD_PIDS[pidKey];
        if (pidInfo && wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ action: 'command', cmd: pidInfo.pid }));
        }
        currentPidIndex.current = (currentPidIndex.current + 1) % ACTIVE_PIDS.length;
      }, 150);
    } else if (connectionMode === 'dcan' && writerRef.current) {
      // BMW K+DCAN raw: wrap each OBD-II Mode 01 PID in a BMW-FAST telegram.
      loggingInterval.current = setInterval(async () => {
        const pidKey = ACTIVE_PIDS[currentPidIndex.current];
        const pidInfo = OBD_PIDS[pidKey];
        if (pidInfo) await sendDCANFrame(pidStringToBytes(pidInfo.pid));
        currentPidIndex.current = (currentPidIndex.current + 1) % ACTIVE_PIDS.length;
      }, 150);
    } else if (writerRef.current) {
      loggingInterval.current = setInterval(async () => {
        const pidKey = ACTIVE_PIDS[currentPidIndex.current];
        const pidInfo = OBD_PIDS[pidKey];
        if (pidInfo) await sendSerialCommand(pidInfo.pid);
        currentPidIndex.current = (currentPidIndex.current + 1) % ACTIVE_PIDS.length;
      }, 150);
    } else {
      // Neither WS nor serial is actually open — surface to the user instead of silently doing nothing.
      setIsLogging(false);
      isLoggingRef.current = false;
      setError('Cannot start logging: no open connection (WebSocket and serial both unavailable). Reconnect and try again.');
    }
  }, [isConnected, connectionMode, sendSerialCommand, sendDCANFrame]);

  return {
    // State
    connectionMode, setConnectionMode,
    isConnected, isLogging, connectionStatus, liveData, logHistory, error, rawResponses,
    // Settings
    thorIP, setThorIP, thorPort, setThorPort,
    radioIP, setRadioIP, radioPort, setRadioPort, radioProtocol, setRadioProtocol,
    baudRate, setBaudRate,
    useDirectBridge, setUseDirectBridge, bridgeUrl, setBridgeUrl,
    // Actions
    handleConnect, disconnect, startLogging, stopLogging,
    sendRawDCAN: sendDCANFrame,
  };
}
